import React, { useState, useEffect, useMemo } from "react";
import { ISingleBarProps, ICapsuleData } from './type.d';
import { ITEM_MARGIN_BOTTOM, LIUBAI_PERCENT } from './constants';
import { getDataDirectionType, getMaxValue } from './util';
import { mathFormat } from '@/pages/strategic/utils/common';
import Capsule from "./Capsule";
import Mark from "./Mark";

export default function MutipleBar(props: ISingleBarProps) {
  const { data, height = 40, coefficient = 1, directionType, maxValue, valueFormatter } = props;
  const [pillarsData, setPillarsData] = useState<ICapsuleData[]>([]);
  const [totalPoint, setTotalPoint] = useState<ICapsuleData>({
    title: '',
    value: 0,
    rate: 0,
    type: 'total'
  });
  const [zeroPoint, setZeroPoint] = useState<ICapsuleData>({
    title: '',
    value: 0,
    rate: 0,
    type: 'zero'
  });
  
  // console.log('=== MutipleBar Initial Data ===');
  // console.log('Input data array:', data);
  // console.log('coefficient:', coefficient);
  // console.log('maxValue (passed from parent):', maxValue);
  
  const computedMaxValue = maxValue ?? getMaxValue(data);

  // 是否存在负数
  const hasNegative = useMemo(() => pillarsData?.some(item => item?.value < 0), [pillarsData]);
  const compHeight = useMemo(() => {
    // Ensure minimum height even when no data (to show empty capsule bar)
    const calculatedHeight = (height + ITEM_MARGIN_BOTTOM) * pillarsData?.length;
    return calculatedHeight > 0 ? calculatedHeight : height;
  }, [height, pillarsData?.length]);
  const dataDirection = useMemo(() => getDataDirectionType(pillarsData, totalPoint), [pillarsData, totalPoint]);
  const hasData = useMemo(
    // value 0  still treat as data
    () => pillarsData?.some((item) => item?.value !== null && item?.value !== undefined && !item?.isHide),
    [pillarsData],
  );

  // 获取分类数据
  const setClassificationData = () => {
    const tmpPillarsData: ICapsuleData[] = [];
    let tmpTotalData: ICapsuleData = {
      title: '',
      value: 0,
      rate: 0,
      type: 'total'
    };
    let tmpZeroPointData: ICapsuleData = {
      title: '',
      value: 0,
      rate: 0,
      type: 'zero'
    };
    
    // Check if any pillar has valid data
    // Determine if we have at least one valid numeric pillar.
    const hasValidData = data?.some(item => 
      item.type === 'pillar' && item.value !== null && item.value !== undefined
    );
    
    data?.forEach(item => {
      // Auto-hide items when no valid pillar data exists
      const shouldHide = item.isHide !== undefined 
        ? item.isHide  // Respect explicit isHide if provided
        : !hasValidData; // Otherwise hide when no valid data
      
      // Prefer preformatted text (already divided into millions), otherwise compute on the fly.
      const formattedValue =
        item.formattedValue ??
        (valueFormatter
          ? valueFormatter(item.value as number)
          : item.value === 0
            ? '0M'
            : mathFormat(item.value, item.precision));

      const newItem: ICapsuleData = {
        ...item,
        formattedValue,
        isHide: shouldHide,
      };
      
      if (item.type === 'pillar' && !shouldHide) {
        tmpPillarsData.push(newItem);
      } else if (item.type === 'total') {
        tmpTotalData = {
          ...newItem,
          markHeight: compHeight - ITEM_MARGIN_BOTTOM,
          liubaiValue: newItem.value * Number(`1.0${LIUBAI_PERCENT}`),
        };
      } else if (item.type === 'zero') {
        tmpZeroPointData = {
          ...newItem,
          markHeight: compHeight - ITEM_MARGIN_BOTTOM,
        };
      }
    });
    setPillarsData(tmpPillarsData);
    setTotalPoint(tmpTotalData);
    setZeroPoint(tmpZeroPointData);
  };

  const getTotalPointPosition = (data: ICapsuleData, hasNegative: boolean) => {
    const value = data?.value ?? 0;
    const trueMaxValue =
      (computedMaxValue || Math.abs(value) || 1) * coefficient;

    // Centered mode: zero sits at 50%, values spread ± from center
    // Used when layout is centerInSameDirection, or data has negative values
    const useCentered =
      directionType === 'centerInSameDirection' || hasNegative || value < 0;

    if (useCentered) {
      return 50 + (value / trueMaxValue) * 50;
    }

    // Left-aligned mode: zero sits at 0%, values spread 0–100%
    return (value / trueMaxValue) * 100;
  };

  useEffect(() => {
    setClassificationData();
  }, [data, compHeight]);

  return (
    <div className="capsule-mutiple-bar" style={{height: `${compHeight || 40}px`}}>
      <div className="capsule-mutiple-bar-left">
        {
          pillarsData.map((item) => (
            <div
              key={item.title}
              style={{height: `${height}px`, marginBottom: `${ITEM_MARGIN_BOTTOM}px`}}
              className="capsule-mutiple-bar-left-title"
            >{item?.title}</div>
          ))
        }
      </div>
      <div className="capsule-mutiple-bar-content">
        {pillarsData?.length > 0 && hasData ? (
          <>
            <Mark
              key={`${zeroPoint?.title}_${Math.abs(zeroPoint?.value)}_${Math.abs(pillarsData[0]?.value)}`}
              data={zeroPoint}
              position={dataDirection === 'mixed' || directionType === 'centerInSameDirection' ? 50 : 0}
            />
            <Mark
              key={`${totalPoint?.title}_${Math.abs(totalPoint?.value)}_${Math.abs(pillarsData[0]?.value)}`}
              data={totalPoint}
              position={getTotalPointPosition(totalPoint, hasNegative)}
            />
            {
              pillarsData.map((item) => (
                <Capsule
                  key={`${item.title}_${item.value}`}
                  data={[item]}
                  totalData={totalPoint}
                  coefficient={coefficient}
                  height={height}
                  maxValue={computedMaxValue}
                  dataDirection={dataDirection}
                  directionType={directionType}
                  showZeroText
                />
              ))
            }
          </>
        ) : (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height,
              width: '100%',
              background: '#F2F3F5',
              borderRadius: '0 30px 30px 0',
            }}
          />
        )}
      </div>
    </div>
  );
}